import React, { useRef, useContext } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
// @ts-ignore
import { ensureLuxonDateOnly } from '../../lib/Formatters';
// @ts-ignore
import Grid from '@mui/material/Grid';
// @ts-ignore
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { UcDatePickerFormik } from 'components/common/UcDatePicker';
import { AccountContext } from 'lib/AccountProvider';
import { Typography } from '@mui/material';
import { historicalComparisonValidationSchema } from './HistoricalComparisonChartUtils';
import './HistoricalComparisonFilter.scss';

interface HistoricalComparisonFilterProps { 
    defaultValues: any;
    onSubmit: (values: any) => void;
    dialogSubmit: number;
    chartType: string;
}

export const HistoricalComparisonFilter = (props: HistoricalComparisonFilterProps) => {
    const dialogSubmit = useRef(props.dialogSubmit);
    const { t } = useTranslation();
    const today = new Date();
    today.setDate(today.getDate() - 1);

    const formRef = useRef<any>(null);
    const { account } = useContext(AccountContext);

    const minDate = account?.minStartTimestamp ? new Date(account.minStartTimestamp) : new Date();
    const maxDate = account?.maxEndTimestamp ? new Date(account.maxEndTimestamp) : new Date();

    React.useEffect(() => {
        if (props.dialogSubmit !== dialogSubmit.current) {
            dialogSubmit.current = props.dialogSubmit;
            if (formRef.current) {
                formRef.current.handleSubmit();
            }
        }
    });

    React.useEffect(() => {
        if (formRef.current) {
            // Keep track of chart type in form for the validation schema
            formRef.current.setFieldValue('chartType', props.chartType);
        }
    }, [props.chartType]);

    return (
        <div className="ignoreCustomFormStyles" data-cy-id="historicalComparisonFilter">
            <Formik
                innerRef={formRef}
                enableReinitialize
                initialValues={{ ...props.defaultValues, chartType: props.chartType }}
                validationSchema={historicalComparisonValidationSchema}
                onSubmit={(values) => {
                    values.account = account;
                    props.onSubmit(values);
                }}
            >
                <Form style={{paddingTop: '6px'}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={6}>
                            <Field
                                dataCyId={"historicalComparisonFilter-yearOne"}
                                label={"Year One"}
                                name={'yearOne'}
                                dateOnly={true}
                                disabled={false}
                                component={UcDatePickerFormik}
                                minDate={ensureLuxonDateOnly(minDate)}
                                maxDate={ensureLuxonDateOnly(maxDate)}
                                format={"yyyy"}
                                views={['year']}
                            />
                            
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <Field
                                dataCyId="historicalComparisonFilter-yearTwo"
                                label={"Year Two"}
                                name={'yearTwo'}
                                dateOnly={true}
                                disabled={false}
                                defaultValue={today}
                                component={UcDatePickerFormik}
                                minDate={ensureLuxonDateOnly(minDate)}
                                maxDate={ensureLuxonDateOnly(maxDate)}
                                format={"yyyy"}
                                views={['year']}
                            />                         
                        </Grid>
                        <Grid item xs={12}>
                            <ErrorMessage name="yearTwo" component="div" className="formik-field-custom-error-message" />
                        </Grid>                      
                        {props.chartType === "month" && (
                            <>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Typography fontWeight="bold" sx={{ pb: 2 }}>{t("Month Range")} </Typography>
                                    <Field
                                        dataCyId="historicalComparisonFilter-startMonth"
                                        label={"Start Month"}
                                        name={'startMonth'}
                                        dateOnly={true}
                                        disabled={false}
                                        component={UcDatePickerFormik}
                                        format={"MMMM"}
                                        views={['month']}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Field
                                        dataCyId="historicalComparisonFilter-endMonth"
                                        label={"End Month"}
                                        name={'endMonth'}
                                        dateOnly={true}
                                        disabled={false}
                                        component={UcDatePickerFormik}
                                        format={"MMMM"}
                                        views={['month']}
                                    />
                                    <ErrorMessage name="endMonth" component="div" className="formik-field-custom-error-message" />
                                </Grid>
                            </>
                        )}
                        {props.chartType === "day" && (
                            <>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Field
                                        dataCyId="historicalComparisonFilter-startDate"
                                        label={"Start Date Range"}
                                        name={'startDate'}
                                        dateOnly={true}
                                        disabled={false}
                                        component={UcDatePickerFormik}
                                        calendarMonthOnly={true}
                                        format={"MMMM d"}
                                        views={['month', 'day']}
                                        formatDensity="spacious"
                                        openTo="day"
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "small",
                                                inputProps: {
                                                    placeholder: "e.g. June 3"
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Field
                                        dataCyId="historicalComparisonFilter-endDate"
                                        label={"End Date Range"}
                                        name={'endDate'}
                                        dateOnly={true}
                                        disabled={false}
                                        component={UcDatePickerFormik}
                                        calendarMonthOnly={true}
                                        format={"MMMM d"}
                                        views={['month', 'day']}
                                        formatDensity="spacious"
                                        openTo="day"
                                        slotProps={{
                                            textField: {
                                                variant: "outlined",
                                                size: "small",
                                                inputProps: {
                                                    placeholder: "e.g. June 3"
                                                }
                                            }
                                        }}
                                    />
                                    <ErrorMessage name="endDate" component="div" className="formik-field-custom-error-message" />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Form>
            </Formik>
        </div>
    );
};